import type {
  AccessLens,
  AccessRole,
  FeatureShape,
  FeatureView,
  ScopeValues,
} from "~/utils/effectiveAccess";
import { NAV_MANIFEST, type NavEntry } from "~/utils/navManifest";

import type { AccessPayload } from "./payload";

export type Lens = FeatureView;

/** Whether the selected role, in the current lens, sees a registry id. */
export type CanSee = (featureId: string) => boolean;

export const NO_PACKAGES_ROUTE = "/no-packages" satisfies NavEntry["to"];

/**
 * The roles a person can pick and open in here. A role held through a share
 * lends its features to the server's checks only, so nothing renders from it.
 */
export const localRolesOf = (payload: AccessPayload): AccessRole[] =>
  payload.roles.filter((role) => role.via === undefined);

/** The picked role when the payload still lists it, else the default. */
export const roleOf = (
  payload: AccessPayload,
  selectedRoleId: string | null | undefined,
): AccessRole | undefined => {
  const roles = localRolesOf(payload);
  return (
    roles.find((role) => role.id === selectedRoleId) ??
    roles.find((role) => role.id === payload.defaultRole) ??
    roles[0]
  );
};

/** A lens the payload lets the person toggle follows their last choice. */
export const lensOf = (lens: AccessLens, isAdminViewStored: boolean): Lens => {
  if (!lens.canToggle) return lens.default;
  return isAdminViewStored ? "admin" : "supplier";
};

export const membershipOf = (
  role: AccessRole | undefined,
  lens: Lens,
  features: FeatureShape[],
): CanSee => {
  const held = new Set(role?.features ?? []);
  const viewsById = new Map(
    features.map((feature) => [feature.id, feature.views]),
  );
  return (featureId) => {
    if (!held.has(featureId)) return false;
    const views = viewsById.get(featureId);
    return views == null || views.includes(lens);
  };
};

export const visibleNavEntries = (canSee: CanSee): NavEntry[] =>
  NAV_MANIFEST.filter((entry) => canSee(entry.id));

export const visibleTabs = <T extends { id: string }>(
  tabs: readonly T[],
  canSee: CanSee,
): T[] => tabs.filter((tab) => canSee(tab.id));

const GMP_BOOKINGS_NAV_ID = "gmp-lamb-bookings";

export const landingRouteOf = (
  canSee: CanSee,
  hasSupplyAgreementToComplete: boolean,
): NavEntry["to"] => {
  const entries = visibleNavEntries(canSee);
  if (entries.length === 0) return NO_PACKAGES_ROUTE;
  if (hasSupplyAgreementToComplete) {
    const bookings = entries.find((entry) => entry.id === GMP_BOOKINGS_NAV_ID);
    if (bookings) return bookings.to;
  }
  return entries[0].to;
};

/**
 * A feature shows the empty-scope screen only when every dataset it reads is
 * scoped and flagged empty; one unscoped read keeps it rendering, so a vendor
 * with no PIC still reaches the bookings tab where their PIC is captured.
 */
export const isFeatureScopeEmpty = (
  feature: FeatureShape | undefined,
  scopeEmpty: AccessPayload["scopeEmpty"],
): boolean => {
  const reads = feature?.reads ?? [];
  return (
    reads.length > 0 && reads.every((read) => scopeEmpty[read.dataset] === true)
  );
};

export interface GmpClientPicker {
  canSelectClient: boolean;
  /** The CLIENT value the filter starts on: "all", or the one allowed. */
  defaultClient: string;
}

const GMP_CLIENT_COLUMN = "CLIENT";
const ALL_CLIENTS = "all";

/**
 * The GMP client filter from the caller's row scope: open when the scope lifts
 * CLIENT or admits more than one value, fixed to the one value otherwise.
 */
export const gmpClientPickerOf = (
  gmpScope: ScopeValues | undefined,
): GmpClientPicker => {
  const clients = gmpScope?.[GMP_CLIENT_COLUMN];
  if (clients === "*" || (clients !== undefined && clients.length > 1)) {
    return { canSelectClient: true, defaultClient: ALL_CLIENTS };
  }
  return { canSelectClient: false, defaultClient: clients?.[0] ?? "gl" };
};
